import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';

export interface User {
  username: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  token: string | null;
  login: (username: string, password: string, turnstile_token?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Проверяем есть ли токен в localStorage при запуске
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('authToken');
      if (savedToken) {
        try {
          // Декодируем токен для проверки структуры и получения данных
          const payload = JSON.parse(atob(savedToken.split('.')[1]));

          // Проверяем не истек ли токен
          const currentTime = Math.floor(Date.now() / 1000);
          if (payload.exp && payload.exp > currentTime) {
            // Токен валидный
            authService.setAuthToken(savedToken);
            setToken(savedToken);
            setUser({ username: payload.sub || 'admin' });
          } else {
            // Токен истек - очищаем
            localStorage.removeItem('authToken');
            authService.setAuthToken(null);
          }
        } catch (error) {
          // Если ошибка при декодировании - очищаем токен
          localStorage.removeItem('authToken');
          authService.setAuthToken(null);
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (username: string, password: string, turnstile_token?: string): Promise<void> => {
    try {
      const response = await authService.login(username, password, turnstile_token);
      const { access_token, token_type } = response;
      
      localStorage.setItem('authToken', access_token);
      authService.setAuthToken(access_token);
      setToken(access_token);
      setUser({ username });
    } catch (error: any) {
      // Обработка специфичных ошибок
      if (error.response?.status === 429) {
        const retryAfter = error.response.headers['retry-after'] || error.response.headers['retry_after'];
        const minutes = Math.ceil(retryAfter / 60);
        throw new Error(`Слишком много попыток входа. Попробуйте через ${minutes} минут.`);
      }
      
      if (error.response?.status === 401) {
        throw new Error('Неверное имя пользователя или пароль');
      }
      
      throw new Error('Ошибка подключения к серверу');
    }
  };

  const logout = async (): Promise<void> => {
    await authService.logout();
    localStorage.removeItem('authToken');
    authService.setAuthToken(null);
    setToken(null);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    token,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};